// noinspection DuplicatedCode
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";

import { Colors } from "../../../assets/Colors";
import { Card } from "@material-tailwind/react";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { useForm } from "../../../components/isRequired";
import { useQuery } from "@tanstack/react-query";
import { fetchGet } from "../../../fetchers";
import { maxPageGlobal } from "../../../assets/config";
import { NewDownloadExcel } from "../../../components/tablecard/NewTableMechanise";
import Skeltons from "../../../components/loading/Skeltons";
import debounce from "lodash/debounce";

const MonitoringPurchaseOrder = () => {
  const initialState = {
    Bulan: new Date(),
    BisnisUnitID: "U0002",
    Bagian: "B",
  };

  const validations = [];

  const { values, errors, touched, customHandler } = useForm(
    initialState,
    validations
  );

  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(maxPageGlobal);
  const [totalPage, setTotalPage] = useState(1);

  const [param, setParam] = useState({
    BisnisUnitID: "U0002",
    Bulan: (new Date().getMonth() + 1).toString(),
    Tahun: new Date().getFullYear().toString(),
    Bagian: "B",
  });

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value) => {
        setSearchQuery(value);
        setIsSearching(false);
      }, 800),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const {
    data: monitoringData,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [
      "monitoring-po",
      param.BisnisUnitID,
      param.Bulan,
      param.Tahun,
      param.Bagian,
      page,
      limit,
      searchQuery,
    ],
    queryFn: () =>
      fetchGet(
        `Procurement/Monitoring/PurchaseOrder/${param.BisnisUnitID}/${param.Bulan}/${param.Tahun}/${param.Bagian}/${encodeURIComponent(
          searchQuery
        )}?page=${page}&limit=${limit}&search=${encodeURIComponent(searchQuery)}`
      ),
    keepPreviousData: true,
    staleTime: 30000,
    onSuccess: (res) => {
      if (res?.meta?.total_page) {
        setTotalPage(res.meta.total_page);
      } else {
        setTotalPage(1);
      }
    },
  });

  const header = useMemo(
    () => [
      { name: "No. MSR", key: "PurchaseRequisitionID", modifiers: "Text" },
      { name: "Tanggal MSR", key: "DatePR", modifiers: "Date" },
      { name: "Keterangan", key: "Remarks", modifiers: "Text" },
      { name: "Bagian", key: "NamaKaryawan", modifiers: "Text" },
      { name: "Pemohon", key: "Requester", modifiers: "Text" },
      { name: "Tipe", key: "Tipe", modifiers: "Text" },
      { name: "No. PRC", key: "MPRComparisonID" },
      { name: "No. PO", key: "PurchaseOrderID" },
      { name: "Tanggal PO", key: "TanggalPO", modifiers: "Date" },
      { name: "Nama Supplier", key: "NamaBagian", modifiers: "Text" },
      { name: "Action", key: "Lihat", modifiers: "LihatPO" },
    ],
    []
  );

  const handleSearchChange = useCallback(
    (input) => {
      const value =
        typeof input === "string" ? input : input?.target?.value || "";
      setSearchInput(value);
      debouncedSetSearch.cancel();

      if (value.trim() === "") {
        setSearchQuery("");
        setPage(1);
        setIsSearching(false);
      } else {
        setIsSearching(true);
        debouncedSetSearch(value);
      }
    },
    [debouncedSetSearch]
  );

  const filterApi = useCallback(() => {
    debouncedSetSearch.cancel();
    setSearchInput("");
    setSearchQuery("");
    setPage(1);
    setIsSearching(false);
    setParam({
      BisnisUnitID: values.BisnisUnitID,
      Bulan: (new Date(values.Bulan).getMonth() + 1).toString(),
      Tahun: new Date(values.Bulan).getFullYear().toString(),
      Bagian: values.Bagian || "B",
    });
  }, [values, debouncedSetSearch]);

  const nextPage = useCallback(() => {
    if (page < totalPage) setPage((prev) => prev + 1);
  }, [page, totalPage]);

  const prevPage = useCallback(() => {
    if (page > 1) setPage((prev) => prev - 1);
  }, [page]);

  const showLoading = isLoading && !monitoringData;

  return (
    <>
      <div className="md:ml-64">
        <div
          style={{ background: Colors.primary }}
          className="pt-14 pb-16 px-3 md:px-8 h-auto"
        >
          <div className="container mx-auto max-w-full" />
        </div>
        <div className="px-3 md:px-8 h-auto -mt-32">
          <div className="container mx-auto max-w-full">
            <div className="grid grid-cols-1 px-4">
              <Card>
                <div className="flex flex-row flex-wrap items-start justify-start w-full px-2 py-2">
                  <div className={"w-1/4"}>
                    <HrdInputGenerator
                      type={"BisnisUnit"}
                      name={"BisnisUnitID"}
                      title={"BisnisUnit"}
                      value={values}
                      required={true}
                      onChange={customHandler}
                      touched={touched}
                      errors={errors}
                      size={"full"}
                    />
                  </div>
                  <div className={"w-1/4"}>
                    <HrdInputGenerator
                      type={"bulan"}
                      name={"Bulan"}
                      title={"Bulan"}
                      value={values}
                      required={true}
                      onChange={customHandler}
                      touched={touched}
                      errors={errors}
                      size={"full"}
                    />
                  </div>
                  <div className={"w-1/4"}>
                    <HrdInputGenerator
                      type={"Bagian"}
                      name={"Bagian"}
                      title={"Bagian"}
                      value={values}
                      required={true}
                      onChange={customHandler}
                      touched={touched}
                      errors={errors}
                      size={"full"}
                    />
                  </div>
                  <div className={"w-1/4"}>
                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                      Buka data
                    </label>

                    <button
                      type="button"
                      onClick={filterApi}
                      className="text-main-100 hover:text-white border border-main-100 hover:bg-main-100 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-7 py-2 text-center mr-2 mb-2"
                    >
                      Buka
                    </button>
                  </div>
                </div>

                <div className="flex flex-col content-center">
                  {showLoading ? (
                    <div className="w-full pt-0 items-left">
                      <Skeltons />
                    </div>
                  ) : monitoringData ? (
                    <div key={"monitoring-po-table"}>
                      <NewDownloadExcel
                        data={monitoringData}
                        header={header}
                        menuController={handleSearchChange}
                        value={searchInput}
                        name={"Nama"}
                        title={"PO"}
                        keys={"monitoring-po-table"}
                        isFetching={isFetching}
                      />

                      {searchInput !== searchQuery && searchInput && (
                        <div className="px-4 text-xs text-blue-500">
                          Mengetik "{searchInput}"...
                        </div>
                      )}

                      {searchQuery && !isSearching && (
                        <div className="px-4 text-xs text-green-600">
                          Menampilkan hasil untuk "{searchQuery}"
                        </div>
                      )}

                      <div className="flex flex-row justify-end items-center gap-4 px-4 py-4">
                        <button
                          onClick={prevPage}
                          disabled={page === 1}
                          className="border px-4 py-1 rounded disabled:opacity-50"
                        >
                          Prev
                        </button>

                        <span className="text-sm">
                          Page {page} / {totalPage}
                          {isFetching && (
                            <span className="ml-2 text-xs text-gray-400">
                              memuat...
                            </span>
                          )}
                        </span>

                        <button
                          onClick={nextPage}
                          disabled={page >= totalPage}
                          className="border px-4 py-1 rounded disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MonitoringPurchaseOrder;
